import React, { useCallback, useEffect, useState } from 'react';
import { trainingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import UploadFileDialog from './UploadFileDialog';
import UnlockManager from './UnlockManager';

export default function DocumentsTab({ meta }) {
  const { user } = useAuth();
  const [files, setFiles] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [openingId, setOpeningId] = useState(null);

  const load = useCallback(() => {
    trainingApi.files('document')
      .then(r => setFiles(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento documenti'); setFiles([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const openFile = async (f) => {
    setOpeningId(f.file_id);
    try {
      const r = await trainingApi.downloadFile(f.file_id);
      window.open(URL.createObjectURL(new Blob([r.data], { type: f.content_type || 'application/pdf' })), '_blank');
    } catch {
      toast.error('Errore apertura file');
    } finally {
      setOpeningId(null);
    }
  };

  const doDelete = async (f) => {
    if (!window.confirm(`Eliminare il documento "${f.title}"?`)) return;
    try {
      await trainingApi.deleteFile(f.file_id);
      toast.success('Documento eliminato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eliminazione fallita'); }
  };

  const fmtSize = (n) => n ? (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`) : '';
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('it-IT') : '—';

  return (
    <div data-testid="documents-tab">
      {meta.can_manage && files && files.length > 0 && (
        <UnlockManager files={files}
          title="Visibilità documenti per dipendente"
          description="I dipendenti vedono solo i documenti sbloccati per loro: quelli non sbloccati sono completamente invisibili. Seleziona un dipendente e attiva i documenti da rendere visibili."
          noun="Documento" nounPlural="documenti" testPrefix="doc" />
      )}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          {files ? `${files.length} documenti` : ''}
        </p>
        {meta.can_manage && (
          <Button onClick={() => setShowUpload(true)} className="gap-1.5" data-testid="upload-document-btn">
            <Upload className="w-4 h-4" /> Carica documento
          </Button>
        )}
      </div>

      {files === null ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : files.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="documents-empty">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>Nessun documento formativo</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {files.map(f => (
            <div key={f.file_id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`document-row-${f.file_id}`}>
              <FileText className="w-5 h-5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-medium text-slate-900">{f.title}</p>
                <p className="text-[11px] text-slate-400">{f.filename} · {fmtSize(f.size)} · caricato il {fmtDate(f.uploaded_at)}</p>
              </div>
              <div className="flex gap-1.5">
                {user?.role === 'operations_manager' && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600"
                          onClick={() => doDelete(f)} data-testid={`document-delete-${f.file_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="outline" size="sm" className="h-8 rounded-none gap-1.5"
                        onClick={() => openFile(f)} disabled={openingId === f.file_id}
                        data-testid={`document-open-${f.file_id}`}>
                  {openingId === f.file_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Apri
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadFileDialog kind="document" onClose={() => setShowUpload(false)}
                          onUploaded={() => { setShowUpload(false); load(); }} />
      )}
    </div>
  );
}
